const SERVICE_WORKER_URL = "/sw.js";
const LEGACY_SERVICE_WORKER_HINTS = ["/agent-runtime-sw.js"];
const RECOVERY_KEY = "sgprop-sw-recovery-attempted";
const STARTUP_WATCHDOG_MS = 7000;

const isAgentPortalPath = (pathname) => /^\/agent\/[^/]+/i.test(String(pathname || ""));

const shouldUseStartupRecovery = () => {
  if (typeof window === "undefined") return false;
  return isAgentPortalPath(window.location.pathname);
};

const isSecureRuntime = () => {
  if (typeof window === "undefined") return false;
  return window.location.protocol === "https:" || ["localhost", "127.0.0.1"].includes(window.location.hostname);
};

const getRegistrationScriptUrl = (registration) => registration?.active?.scriptURL || registration?.waiting?.scriptURL || registration?.installing?.scriptURL || "";

const unregisterLegacyServiceWorkers = async () => {
  const registrations = await navigator.serviceWorker.getRegistrations();
  const legacyRegistrations = registrations.filter((registration) => LEGACY_SERVICE_WORKER_HINTS.some((hint) => getRegistrationScriptUrl(registration).includes(hint)));

  await Promise.all(legacyRegistrations.map((registration) => registration.unregister()));

  if (!("caches" in window)) return;
  const cacheNames = await caches.keys();
  await Promise.all(cacheNames.filter((cacheName) => cacheName.startsWith("agent-")).map((cacheName) => caches.delete(cacheName)));
};

const recoverBlankShell = async () => {
  if (!shouldUseStartupRecovery()) {
    return;
  }

  if (typeof window === "undefined" || window.sessionStorage.getItem(RECOVERY_KEY) === "1") {
    return;
  }

  window.sessionStorage.setItem(RECOVERY_KEY, "1");
  window.dispatchEvent(new CustomEvent("sgprop-portal-recovery-start", {
    detail: {
      reason: "stale-cache",
      message: "We’re clearing a stale startup state so your portal can reopen cleanly.",
    },
  }));

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));

    if ("caches" in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
    }
  } catch (error) {
    console.warn("[PWA] Recovery cleanup failed", error);
  }

  const recoveryUrl = new URL(window.location.href);
  recoveryUrl.searchParams.set("sw-recovery", Date.now().toString());
  window.location.replace(recoveryUrl.toString());
};

const startBlankShellRecoveryWatchdog = () => {
  if (!shouldUseStartupRecovery()) {
    return;
  }

  window.setTimeout(() => {
    const root = document.getElementById("root");
    const hasRenderedChildren = Boolean(root?.children?.length || root?.innerHTML?.trim()?.length);

    if (!hasRenderedChildren) {
      void recoverBlankShell();
    }
  }, STARTUP_WATCHDOG_MS);
};

export const registerPwaServiceWorker = () => {
  if (typeof window === "undefined" || !("serviceWorker" in navigator) || !isSecureRuntime()) {
    return;
  }

  const register = async () => {
    try {
      await unregisterLegacyServiceWorkers();
      const registration = await navigator.serviceWorker.register(SERVICE_WORKER_URL, { scope: "/", updateViaCache: "none" });
      console.info("[PWA] Service Worker registered", registration.scope);
      await registration.update();
      await navigator.serviceWorker.ready;
      console.info("[PWA] Service Worker ready");
    } catch (error) {
      console.error("[PWA] Service Worker registration failed", error);
    }
  };

  if (document.readyState === "complete") {
    startBlankShellRecoveryWatchdog();
    void register();
    return;
  }

  window.addEventListener("load", () => {
    startBlankShellRecoveryWatchdog();
    void register();
  }, { once: true });
};